import React from "react";
import { Link } from "react-router-dom";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { ExternalLink, LogIn, Sparkles, Mail, Hammer, Lightbulb, Box } from "lucide-react";

const ETCHERY_URL = "https://theetcheryco.nz/";

export default function EtcheryPartnershipPage() {
  return (
    <div className="club-theme min-h-screen" data-testid="etchery-partnership-page">
      <PublicHeader />

      {/* HERO */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 pt-20 pb-12 text-center" data-testid="etchery-hero">
        <div className="bloom-divider mb-6">✦ A Bloom Club Partnership ✦</div>
        <div className="flex justify-center mb-8">
          <img
            src="/etchery-logo.webp"
            alt="The Etchery & Co"
            className="w-56 md:w-72 object-contain"
            data-testid="etchery-logo"
          />
        </div>
        <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] max-w-3xl mx-auto">
          A maker, two flower brands, <em className="italic">and one big idea.</em>
        </h1>
        <p className="font-serif italic text-xl text-[var(--bloom-ink-soft)] mt-6 max-w-2xl mx-auto">
          The Etchery &amp; Co is our trusted partner across both Heritage Dahlias and the Heritage Bloom Club. Less waste, more time in the garden, better made things.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href={ETCHERY_URL}
            target="_blank"
            rel="noreferrer"
            className="bloom-btn bloom-btn-primary"
            data-testid="etchery-website-cta"
          >
            <ExternalLink size={14} /> Visit The Etchery &amp; Co
          </a>
          <Link to="/login" state={{ etchery: true }} className="bloom-btn bloom-btn-outline" data-testid="etchery-signin-cta">
            <LogIn size={14} /> Etchery sign in
          </Link>
        </div>
      </section>

      {/* DIVIDER */}
      <div className="flex justify-center mb-8" data-testid="etchery-divider">
        <div className="club-flower-divider max-w-sm"><span>✦</span></div>
      </div>

      {/* THE PARTNERSHIP */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 py-12" data-testid="etchery-about">
        <div className="bloom-label mb-3 text-center">✦ The partnership ✦</div>
        <h2 className="font-serif italic text-3xl md:text-4xl text-center mb-10">Less waste, more time in the garden.</h2>

        <div className="space-y-6 font-serif text-lg text-[var(--bloom-ink-soft)] leading-[1.85]">
          <p>
            We don&rsquo;t just want to grow good flowers, we want to grow them with less stuff thrown away at the end of the season. That&rsquo;s where The Etchery &amp; Co comes in.
          </p>
          <p>
            Hannah and her team don&rsquo;t just make products, they solve problems. From mechanical parts and workshop solutions to clever hacks that make everyday life easier, if it can be designed and made, they&rsquo;re keen to give it a go.
          </p>
          <p>
            Together we&rsquo;re building tools that last. Hand-etched plant tags that don&rsquo;t fade in a season. Watering blocks that mean less time hand-watering and more time enjoying the bloom. And next, a fully reusable flat-lay flower box, designed to be returned, refilled, and used again.
          </p>
          <p className="italic text-center text-[var(--bloom-ink)]">
            Useful, well-made things, designed to be used again. Made right here in Aotearoa.
          </p>
        </div>
      </section>

      {/* THE PRODUCTS */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-12" data-testid="etchery-products">
        <div className="text-center mb-12">
          <div className="bloom-label mb-3">✦ What we&rsquo;re making together ✦</div>
          <h2 className="font-serif italic text-3xl md:text-4xl">Three products, one philosophy.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <ProductCard
            badge="Heritage Dahlias"
            badgeClass="bg-[var(--bloom-ink)] text-white"
            title="Custom plant tags"
            body="Hand-etched, weather-tough plant tags that stay legible through a full season and the next. Designed for Heritage Dahlias garden customers and growers."
            testid="product-plant-tags"
          />
          <ProductCard
            badge="Heritage Bloom Club"
            badgeClass="bg-[var(--bloom-brand)] text-white"
            title="Watering blocks"
            body="A clever water-holding block sized for the Bloom Club&rsquo;s weekly subscription stems. Means fewer trips to the tap and a longer vase life, without any single-use foam."
            testid="product-watering-blocks"
          />
          <ProductCard
            badge="Coming soon · Bloom Club"
            badgeClass="bg-[#C9A14A] text-white"
            title="Reusable flat-lay flower box"
            body="A fully reusable, returnable Bloom Club flower box. Built to travel, hold flat-lay stems beautifully, and come back for next week&rsquo;s box. Less cardboard, more flowers."
            testid="product-flat-lay-box"
          />
        </div>
      </section>

      {/* WHAT THEY DO */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-12" data-testid="etchery-what">
        <div className="text-center mb-12">
          <div className="bloom-label mb-3">✦ What they do ✦</div>
          <h2 className="font-serif italic text-3xl md:text-4xl">Got an idea? That&rsquo;s their favourite starting point.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <Pillar icon={Lightbulb} title="From a sketch or a photo" body="A rough idea, a Pinterest find, a broken part you need replaced. They start where you start." />
          <Pillar icon={Hammer} title="From workshop to home" body="Mechanical parts, custom fixtures, workshop solutions and small batch production for makers and small brands." />
          <Pillar icon={Box} title="Coming soon" body="Laser cutting and engraving, opening the door to even more materials, finishes and personalised creations." />
        </div>
      </section>

      {/* QUOTE STRIP */}
      <section className="py-16" data-testid="etchery-quote">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Sparkles className="mx-auto mb-6 text-[var(--bloom-accent)]" strokeWidth={1.2} size={28} />
          <blockquote className="font-serif italic text-2xl md:text-3xl leading-[1.3] text-[var(--bloom-ink)]">
            &ldquo;We&rsquo;re always open to new projects and love a challenge. So don&rsquo;t overthink it. Send us a message and let&rsquo;s see what we can create together.&rdquo;
          </blockquote>
          <div className="mt-6 text-[10px] tracking-[0.3em] uppercase text-[var(--bloom-ink-muted)]">
            ✦ The Etchery &amp; Co ✦
          </div>
        </div>
      </section>

      {/* CTAs */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-24" data-testid="etchery-ctas">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bloom-card p-8 flex flex-col">
            <div className="bloom-label mb-3">For customers</div>
            <h3 className="font-serif text-3xl mb-3">Visit the shop</h3>
            <p className="font-serif italic text-base text-[var(--bloom-ink-soft)] mb-6 flex-1">
              Browse what The Etchery has already brought to life, or send them your own idea. From image to real life, they&rsquo;ll work with you to make it happen.
            </p>
            <a href={ETCHERY_URL} target="_blank" rel="noreferrer" className="bloom-btn bloom-btn-primary justify-center" data-testid="etchery-shop-cta">
              <ExternalLink size={14} /> theetcheryco.nz
            </a>
          </div>

          <div className="bloom-card p-8 flex flex-col">
            <div className="bloom-label mb-3">For Hannah</div>
            <h3 className="font-serif text-3xl mb-3">Etchery sign in</h3>
            <p className="font-serif italic text-base text-[var(--bloom-ink-soft)] mb-6 flex-1">
              The partnership console, with tag products, orders, watering block development and shared notes. Everything we&rsquo;re building together, in one place.
            </p>
            <Link to="/login" state={{ etchery: true }} className="bloom-btn bloom-btn-outline justify-center" data-testid="etchery-portal-cta">
              <LogIn size={14} /> Sign in to the partnership console
            </Link>
            <div className="mt-4 text-xs text-[var(--bloom-ink-muted)] font-serif italic flex items-center gap-2">
              <Mail size={12} /> Lost your password? Email heritagebloomclub@gmail.com
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

function Pillar({ icon: Icon, title, body }) {
  return (
    <div className="text-center md:text-left">
      <Icon size={24} strokeWidth={1.4} className="mb-4 text-[var(--bloom-brand)] mx-auto md:mx-0" />
      <h3 className="font-serif text-2xl leading-tight mb-3">{title}</h3>
      <p className="font-serif text-base text-[var(--bloom-ink-soft)] leading-relaxed italic">{body}</p>
    </div>
  );
}

function ProductCard({ badge, badgeClass, title, body, testid }) {
  return (
    <div className="bloom-card p-6 flex flex-col" data-testid={testid}>
      <span className={`text-[9px] tracking-[0.25em] uppercase px-2 py-1 self-start mb-4 ${badgeClass}`}>{badge}</span>
      <h3 className="font-serif text-2xl leading-tight mb-3">{title}</h3>
      <p className="font-serif text-base text-[var(--bloom-ink-soft)] italic leading-relaxed">{body}</p>
    </div>
  );
}
